import { createSlice, createAction, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { Basemap, BasemapConfig, Formats } from 'features/maps/types'
import { WrapperWidgetUIProps } from '@carto/meridian-ds/widgets'
import { selectValidSpatialFilter } from '@carto/react-redux'
import { Feature } from 'geojson'
import { isEqual } from 'lodash'
import { shallowEqual } from 'react-redux'

import { KeplerGlSchema, SavedConfigV1 } from '@carto/kepler.gl'
import { MapState } from '@carto/kepler.gl/reducers'

import { ApiError, RequestStatus } from 'features/common'
import { getValueHash } from 'features/common/utils/cryptoHash'
import { ImportingJob } from 'features/jobs/types'
import { BuilderUiPersistenceState, CustomBaseMapSettings, SqlParameter } from 'features/maps/types'
import { DynamicTilingMigrationBackup } from 'features/maps/map-config/map-config'
import {
  AttributeStatsRequest,
  BuilderBasemapStyle,
  LayerRequest,
  FeatureDetails,
  TakeOverResponseState,
  BuilderLockState,
  RightPaneTab,
  WidgetType,
  BuilderMode,
  MeasureTooltip,
  InitBuilderRequestParams,
  Widget,
  LegendSettings,
  LayerLegendSettings,
  PopupSettings,
  LayerPopupSettings,
  SQLPanelProps,
  WidgetsMapConfig,
  BuilderDataset,
  AISettings,
  BuilderAgentThread,
  BuilderAgentConversation,
  BuilderAgentConversationItem,
  BuilderAgentAuthor,
  DEFAULT_MAP_INDEX,
  WidgetStateType,
  BuilderAgentLayer,
  BuilderAgentDataset,
  BuilderAgentResourceState,
  KeplerTable,
  KeplerLayer,
  BuilderLayerWithData,
  LegendHexColorMaps
} from 'features/builder/types'
import { RootState } from 'app/state/store'
import { Connection, ProviderType } from 'features/connections'
import { FeatureCollection } from '@deck.gl-community/editable-layers'
import { BasemapSettings } from 'features/account/types'
import { defaultBasemapsSettings } from '../ui/builderConstants'
import { DEFAULT_MAP_STYLE_PLACEHOLDER, defaultVisibleLayerGroups } from '../ui/KeplerGl/defaultMapStyles'
import { basemapConfigSelector } from '../model/mapStyles'
import { getFiltersWithoutTimeType } from './widgetsUtils'
import { Layer } from '@carto/kepler.gl/layers'
import { getNewDatasetColor } from './datasetUtils'
import { blockAutoSaveDocMigrationTemporary } from '../model/documentModeMigration'
import { checkDataSourceWidgetCompatibility } from '../utils/widgetUtils'
import { removeAgentLayersFromMapConfig } from './agentState'
import { basemapIdToStyleId, basemapToMapStyle } from '../model/basemap'
import { createEntitySelector, createMemoizeById } from 'app/state/selectorUtils'
import { serializedKeplerLayerMemoized } from '../ui/layers/keplerLayerConfig'

export const name = 'builder'

interface MeasureToolPopup {
  x: number
  y: number
  totalDistance: number
}

export type MeasureToolState = {
  enabled: boolean
  layer?: FeatureCollection
  tooltips?: MeasureTooltip[]
  guides?: FeatureCollection
  popup?: MeasureToolPopup
}

export type LayerState = {
  // isConfigActive: boolean, TODO it should be here, not in layer.config
  isLoadingTiles?: boolean
  spatialIndexResolution?: number
  highlightGeometry?: Feature | null
  highlightGeometryStyle?: 'hover' | 'click'

  // this is used to store hex color legend settings which cannot be loaded
  // in public map mode as there is no random access to query dataset
  legendHexColorMaps?: LegendHexColorMaps
}

export interface BuilderState extends BuilderUiPersistenceState {
  isLoading: boolean
  saveNeeded: boolean
  saveMapError?: ApiError
  sqlPanel?: SQLPanelProps
  draftQuery?: string
  builderMode: BuilderMode
  previewMode: boolean
  lastThumbnailMapConfigHash?: string
  widgets: Widget[]
  selectedWidgetId?: string

  /**
   * Currently selected `BuilderMapStyle.id` and enabled layer groups.
   */
  basemapConfig: BasemapConfig

  /**
   * Exhaustive list of map styles (carto, google, custom) and their status.
   * Remote MapLibre styles are loaded into memory.
   * Failed styles are marked as invalid.
   *
   * Derives from `defaultMapStyles`, `customBasemaps` and `customBaseMaps.customStyle` in case of public maps.
   */
  mapStyles: BuilderBasemapStyle[]
  mapStylesStatus: RequestStatus

  /**
   * If selected map is custom (or previously amazon), it stored map settings.
   *
   * (this doesn't have to be in state actually, but it's here as it's convenient to have it here,
   * it makes getMapConfig easier)
   */
  customBaseMaps?: CustomBaseMapSettings

  /**
   * Defines which of basemap providers (Carto, Google, Custom) are enabled in account.
   */
  basemapSettings: BasemapSettings

  /** Custom basemaps defined for account. Only loaded if custom is enabled in `basemapSettings`. */
  customBasemaps: Basemap[]

  legendSettings?: LegendSettings
  popupSettings?: PopupSettings
  datasetAttributeStatsOnRequest: Record<string, Array<string>>
  featureDetailsPanel?: FeatureDetails
  sqlParameters: SqlParameter[]
  currentMapLockStatus?: BuilderLockState
  takeOverResponseState: TakeOverResponseState
  layersUpdateStatus: Record<string, RequestStatus>
  viewerCredentialsRequired?: Connection[]
  userLocation?: GeoJSON.Position
  isMeasureToolEnabled?: boolean
  measureToolLayer?: FeatureCollection
  measureToolTooltips?: MeasureTooltip[]
  measureToolGuides?: FeatureCollection
  measureToolPopup?: MeasureToolPopup
  timeSeriesDataSourceAnimating: Record<string, boolean>
  mapPasswordRequired?: boolean
  mapPasswordEnterAttempt?: number
  publicMapAuthorizationHeader?: string
  geocoderSearchText?: string

  layerPanelLoadingStatus: RequestStatus
  layerState: Record<string, LayerState>

  takeMapScreenshots: boolean
  // In split mode we have two maps
  mapScreenshots: string[]
  aiSettings: AISettings
  // TODO: Consider renaming to 'agentThread' or 'aiThread'.
  genAiThread?: BuilderAgentThread
  // TODO: Consider renaming to 'agentConversation' or 'aiConversation'.
  genAiConversation: BuilderAgentConversation
  agentMarkerHover?: { x: number; y: number; label: string; locationNumber: number }
  dynamicTilingMigrationBackup?: DynamicTilingMigrationBackup
  dragMapState?: Partial<MapState>
  tilesetWidgetSourceReady?: Record<string, boolean>
  highlighted?: {
    id: string | null
    type: 'dataset' | 'layer' | null
  }
  shouldOpenAgentConversation: boolean
}

export enum QueryActions {
  Update,
  Cancel
}
export interface UpdateQueryProps {
  mapId: string
  datasetId: string
  query: string
  sqlParameters?: SqlParameter[]

  format?: Formats
  provider?: ProviderType
  geoColumn?: string | null
  columns?: string[] | null

  queryAction?: QueryActions
  skipSaveDataset?: boolean
  convertToQuery?: boolean

  createLayer?: boolean
}

const initialState: BuilderState = {
  mapPasswordRequired: false,
  publicMapAuthorizationHeader: undefined,
  builderMode: BuilderMode.Editor,
  previewMode: false,
  isLoading: true,
  saveNeeded: false,
  widgets: [],
  controlsPaneOpen: false,
  datasetAttributeStatsOnRequest: {},
  sqlParameters: [],
  takeOverResponseState: { active: false },
  layersUpdateStatus: {},
  descriptionOpen: false,
  descriptionPreview: false,
  userLocation: undefined,
  isMeasureToolEnabled: false,
  measureToolLayer: undefined,
  measureToolTooltips: undefined,
  measureToolGuides: undefined,
  timeSeriesDataSourceAnimating: {},
  basemapSettings: defaultBasemapsSettings,
  customBasemaps: [],
  mapStyles: [],
  mapStylesStatus: RequestStatus.Idle,
  basemapConfig: {
    styleId: DEFAULT_MAP_STYLE_PLACEHOLDER,
    visibleLayerGroups: defaultVisibleLayerGroups
  },
  layerPanelLoadingStatus: RequestStatus.Idle,
  takeMapScreenshots: false,
  mapScreenshots: [],
  aiSettings: {
    enabled: false,
    mapContext: '',
    conversationStarters: [],
    userGuideEnabled: false,
    userGuide: ''
  },
  genAiConversation: [],
  layerState: {},
  shouldOpenAgentConversation: false,
  agentMarkerHover: undefined
}

export const ADD_FILTER_ACTION = 'carto/addFilter'
export const REMOVE_FILTER_ACTION = 'carto/removeFilter'
export const CLEAR_FILTERS_ACTION = 'carto/clearFilters'
export const ADD_SPATIAL_FILTER_ACTION = 'carto/addSpatialFilter'
export const REMOVE_SPATIAL_FILTER_ACTION = 'carto/removeSpatialFilter'
export const SET_IS_DROPPING_FEATURES = 'carto/setIsDroppingFeatures' // TODO ensure proper export from C4R
export const SET_GEOCODER_RESULT = 'carto/setGeocoderResult'

const asyncActions = {
  initBuilderMap: createAction<InitBuilderRequestParams>(`${name}/init_builder_map`),

  addLayerRequest: createAction<LayerRequest>(`${name}/add_layer_request`),
  addLayerToExistingDatasetRequest: createAction<string>(`${name}/add_layer_to_existing_dataset_request`),

  addQueryRequest: createAction<LayerRequest>(`${name}/add_query_request`),
  updateQueryRequest: createAction<UpdateQueryProps>(`${name}/update_query_request`),
  cancelQueryRequest: createAction<Partial<UpdateQueryProps>>(`${name}/cancel_query_request`),

  refreshDatasetRequest: createAction<string>(`${name}/refresh_dataset_request`),
  refreshDatasetsByConnections: createAction<string[]>(`${name}/refresh_datasets_by_connections`),

  attachViewerCredentials: createAction(`${name}/attach_viewer_credentials`),
  addLayerFromImportedJobRequest: createAction<ImportingJob>(`${name}/add_layer_for_imported_job_request`),
  newWidget: createAction<Widget['dataSource']>(`${name}/new_widget`),
  removeFilterByWidgetRequest: createAction<Widget>(`${name}/remove_filter_by_widget_request`),
  loadAttributeStatsRequest: createAction<AttributeStatsRequest>(`${name}/load_attribute_stats`),
  loadHexColumnStatsRequest: createAction<AttributeStatsRequest>(`${name}/load_hex_column_stats`),
  updateSpatialIndexAggregationRequest: createAction<string>(`${name}/update_spatial_index_aggregation`),
  updateDatasetsWithSqlParametersRequest: createAction<{ parameter?: SqlParameter; skipSaveDataset?: boolean }>(
    `${name}/updateDatasetsWithSqlParametersRequest`
  ),
  unlockMapRequest: createAction<string>(`${name}/unlock_map_request`),
  updateSpatialIndexAggregationResLevelRequest: createAction<{ datasetId: string; aggregationResLevel: number }>(
    `${name}/update_spatial_index_aggregation_res_level`
  ),
  queryThisTableRequest: createAction<{ datasetId: string }>(`${name}/query_this_table_request`),
  convertTableToQueryDatasetRequest: createAction<{ datasetId: string; sqlQuery: string; geoColumn: string }>(
    `${name}/convert_table_to_query_request`
  ),
  aggregateDatasetByGeometry: createAction<{
    datasetId: string
    layerId: string
    geometry: string
    aggregate: boolean
  }>(`${name}/aggregate_dataset_by_geometry`),
  updateLayerFiltersForTimeSeriesAnimationRequest: createAction<{ datasetId: string; isRunning: boolean }>(
    `${name}/configure_filters_for_time_series_animation_request`
  ),
  mapPasswordUpdated: createAction(`${name}/map_password_updated`),
  legendVisibilityChange: createAction<{ layerId: string; isVisible: boolean }>(`${name}/legend_visibility_change`),
  duplicateDatasetRequest: createAction<{ datasetId: string }>(`${name}/duplicate_dataset_request`)
}
export const showGlobalBuilderError = createAction<string>(`${name}/show_global_error`)

let nextNewSqlPanelKey = 0

const reducers = {
  setMapPasswordRequired: (state: BuilderState, action: PayloadAction<boolean>) => {
    state.mapPasswordRequired = action.payload
  },
  setPublicMapAuthorizationHeader: (state: BuilderState, action: PayloadAction<string | undefined>) => {
    state.publicMapAuthorizationHeader = action.payload
  },
  incMapPasswordEnterAttempt: (state: BuilderState) => {
    state.mapPasswordEnterAttempt = (state.mapPasswordEnterAttempt ?? 0) + 1
  },
  showSQLPanel: (state: BuilderState, action: PayloadAction<SQLPanelProps>) => {
    // Do not show SQL panel for AI-created sources.
    if (state.genAiThread?.datasets?.some((d) => d.state === BuilderAgentResourceState.Loading)) {
      return
    }

    state.sqlPanel = action.payload
    if (!state.sqlPanel.key) {
      state.sqlPanel.key = state.sqlPanel.datasetId || `new-sql-query-${String(nextNewSqlPanelKey++)}`
    }
    state.draftQuery = action.payload.query
  },
  setSQLPanelMinimized: (state: BuilderState, action: PayloadAction<boolean>) => {
    if (state.sqlPanel) {
      state.sqlPanel.minimized = action.payload
    }
  },
  hideSQLPanel: (state: BuilderState) => {
    state.sqlPanel = undefined
    state.draftQuery = undefined
  },
  setDraftQuery: (state: BuilderState, action: PayloadAction<string>) => {
    state.draftQuery = action.payload
  },
  resetBuilderState: () => {
    return initialState
  },
  setIsLoading: (state: BuilderState, action: PayloadAction<boolean>) => {
    state.isLoading = action.payload
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setSaveNeeded: (state: BuilderState, _action: PayloadAction<void>) => {
    state.saveNeeded = true
  },
  resetSaveNeeded: (state: BuilderState) => {
    state.saveNeeded = false
  },
  setSaveMapError: (state: BuilderState, action: PayloadAction<ApiError>) => {
    state.saveMapError = action.payload
  },
  setBuilderMode: (state: BuilderState, action: PayloadAction<BuilderMode>) => {
    state.builderMode = action.payload
  },
  setPreviewMode: (state: BuilderState, action: PayloadAction<boolean>) => {
    const newPreviewMode = action.payload
    state.previewMode = newPreviewMode
    state.builderMode = newPreviewMode ? BuilderMode.Viewer : BuilderMode.Editor
  },
  setLastThumbnailMapConfigHash: (state: BuilderState, action: PayloadAction<string>) => {
    state.lastThumbnailMapConfigHash = action.payload
  },
  setWidgets: (state: BuilderState, action: PayloadAction<Widget[]>) => {
    state.widgets = action.payload
  },
  removeWidget: (state: BuilderState, action: PayloadAction<Widget>) => {
    const { id } = action.payload
    state.widgets = state.widgets.filter((w) => w.id !== id)

    if (state.selectedWidgetId === id) {
      state.selectedWidgetId = undefined
    }
  },
  removeWidgetsByDataSource: (state: BuilderState, action: PayloadAction<Widget['dataSource']>) => {
    state.widgets = state.widgets.filter((w) => w.dataSource !== action.payload)
  },
  updateWidgetProps: (
    state: BuilderState,
    action: PayloadAction<{ id: Widget['id']; props: Record<string, unknown> }>
  ) => {
    const { id, props } = action.payload
    const idx = state.widgets.findIndex((w) => w.id === id)

    if (idx >= 0) {
      state.widgets[idx] = { ...state.widgets[idx], ...props }
    }
  },
  updateWidgetWrapperProps: (
    state: BuilderState,
    action: PayloadAction<{ id: string; wrapperProps: Partial<WrapperWidgetUIProps> }>
  ) => {
    const { id, wrapperProps } = action.payload
    const idx = state.widgets.findIndex((w) => w.id === id)

    if (idx >= 0) {
      state.widgets[idx].wrapperProps = { ...state.widgets[idx].wrapperProps, ...wrapperProps }
    }
  },
  replaceWidget: (state: BuilderState, action: PayloadAction<Widget>) => {
    const widget = action.payload
    const idx = state.widgets.findIndex((w) => w.id === widget.id)

    if (idx >= 0) {
      state.widgets[idx] = widget
    }
  },
  setSelectedWidgetId: (state: BuilderState, action: PayloadAction<string | undefined>) => {
    const widgetId = action.payload
    state.selectedWidgetId = action.payload
    if (widgetId) {
      state.descriptionOpen = false
      state.controlsPaneOpen = true
      state.controlsPaneTab = RightPaneTab.Widgets
    }
  },
  setTilesetWidgetSourceReady: (state: BuilderState, action: PayloadAction<{ sourceId: string; ready: boolean }>) => {
    const { sourceId, ready } = action.payload

    state.tilesetWidgetSourceReady = {
      ...state.tilesetWidgetSourceReady,
      [sourceId]: ready
    }
  },
  setControlsPaneOpen: (state: BuilderState, action: PayloadAction<boolean>) => {
    state.controlsPaneOpen = action.payload
  },
  setControlsPaneState: (state: BuilderState, action: PayloadAction<{ open: boolean; tab?: RightPaneTab }>) => {
    state.controlsPaneOpen = action.payload.open
    if (action.payload.open && state.descriptionOpen) {
      state.descriptionOpen = false
    }
    if (action.payload.tab) {
      state.controlsPaneTab = action.payload.tab
    }
  },
  setRightPaneTab: (state: BuilderState, action: PayloadAction<RightPaneTab>) => {
    state.controlsPaneTab = action.payload
  },
  setDescriptionOpen(state: BuilderState, action: PayloadAction<boolean>) {
    state.descriptionOpen = action.payload
  },
  setDescriptionPreview(state: BuilderState, action: PayloadAction<boolean>) {
    state.descriptionPreview = action.payload
  },
  setCustomBaseMapSettings: (state: BuilderState, action: PayloadAction<CustomBaseMapSettings>) => {
    state.customBaseMaps = action.payload
  },
  setLegendSettings: (state: BuilderState, action: PayloadAction<LegendSettings>) => {
    state.legendSettings = action.payload
  },
  setLayerLegendSettings: (
    state: BuilderState,
    action: PayloadAction<{ layerId: string; settings: LayerLegendSettings }>
  ) => {
    state.legendSettings = {
      ...state.legendSettings,
      expanded: state.legendSettings?.expanded || { [DEFAULT_MAP_INDEX]: false },
      layers: {
        ...state.legendSettings?.layers,
        [action.payload.layerId]: action.payload.settings
      }
    }
  },
  setPopupSettings: (state: BuilderState, action: PayloadAction<PopupSettings>) => {
    state.popupSettings = action.payload
  },
  setLayerPopupSettings: (
    state: BuilderState,
    action: PayloadAction<{ layerId: string; settings: LayerPopupSettings }>
  ) => {
    state.popupSettings = {
      ...state.popupSettings,
      layers: {
        ...state.popupSettings?.layers,
        [action.payload.layerId]: action.payload.settings
      }
    }
  },
  moveLayerPopupSettings: (state: BuilderState, action: PayloadAction<{ oldLayerId: string; newLayerId: string }>) => {
    const { oldLayerId, newLayerId } = action.payload
    const movedSettings = state.popupSettings?.layers[oldLayerId]
    if (movedSettings) {
      delete state.popupSettings?.layers[oldLayerId]
      if (!state.popupSettings) {
        state.popupSettings = { layers: {} }
      }
      state.popupSettings.layers[newLayerId] = movedSettings
    }
  },
  setFeatureDetailsPanel: (state: BuilderState, action: PayloadAction<FeatureDetails | undefined>) => {
    state.featureDetailsPanel = action.payload
  },
  closeFeatureDetailsPanel: (state: BuilderState, action: PayloadAction<{ fromMapViewId: number }>) => {
    if (state.featureDetailsPanel?.mapViewId === action.payload.fromMapViewId) {
      state.featureDetailsPanel = undefined
    }
  },
  addDatasetAttributeStatsOnRequest: (
    state: BuilderState,
    action: PayloadAction<{ datasetId: string; attribute: string }>
  ) => {
    const { datasetId, attribute } = action.payload

    if (!state.datasetAttributeStatsOnRequest[datasetId]) {
      state.datasetAttributeStatsOnRequest[datasetId] = []
    }
    state.datasetAttributeStatsOnRequest[datasetId].push(attribute)
  },
  removeDatasetAttributeStatsOnRequest: (
    state: BuilderState,
    action: PayloadAction<{ datasetId: string; attribute: string }>
  ) => {
    const { datasetId, attribute } = action.payload

    if (!state.datasetAttributeStatsOnRequest[datasetId]) {
      return
    }
    state.datasetAttributeStatsOnRequest[datasetId] = state.datasetAttributeStatsOnRequest[datasetId].filter(
      (attr) => attr !== attribute
    )

    if (state.datasetAttributeStatsOnRequest[datasetId].length === 0) {
      delete state.datasetAttributeStatsOnRequest[datasetId]
    }
  },
  loadSqlParameters: (state: BuilderState, action: PayloadAction<SqlParameter[]>) => {
    state.sqlParameters = action.payload
  },
  addSqlParameter: (state: BuilderState, action: PayloadAction<SqlParameter>) => {
    state.sqlParameters.push(action.payload)
    state.controlsPaneOpen = true
    state.controlsPaneTab = RightPaneTab.Parameters
  },
  updateSqlParameter: (state: BuilderState, action: PayloadAction<SqlParameter>) => {
    const index = state.sqlParameters.findIndex((p) => p.id === action.payload.id)
    if (index !== -1) {
      state.sqlParameters[index] = action.payload
    } else {
      state.sqlParameters.push(action.payload)
    }
  },
  removeSqlParameter: (state: BuilderState, action: PayloadAction<SqlParameter>) => {
    state.sqlParameters = state.sqlParameters.filter((p) => p.id !== action.payload.id)
  },
  setCurrentMapLockStatus(state: BuilderState, action: PayloadAction<BuilderLockState | undefined>) {
    state.currentMapLockStatus = action.payload
  },
  updateCurrentMapLockStatus(state: BuilderState, action: PayloadAction<Partial<BuilderLockState>>) {
    state.currentMapLockStatus = { locked: false, ...state.currentMapLockStatus, ...action.payload }
  },
  setTakeOverResponseState(state: BuilderState, action: PayloadAction<TakeOverResponseState>) {
    state.takeOverResponseState = action.payload
  },
  setLayerUpdateStatus(state: BuilderState, action: PayloadAction<{ layerId: string; status: RequestStatus }>) {
    state.layersUpdateStatus = {
      ...state.layersUpdateStatus,
      [action.payload.layerId]: action.payload.status
    }
  },
  setViewerCredentialsRequired(state: BuilderState, action: PayloadAction<Connection[]>) {
    state.viewerCredentialsRequired = action.payload
  },
  setUserLocation(state: BuilderState, action: PayloadAction<GeoJSON.Position | undefined>) {
    state.userLocation = action.payload
  },
  disableMeasureTool(state: BuilderState) {
    if (state.isMeasureToolEnabled) {
      state.isMeasureToolEnabled = false
      state.measureToolLayer = undefined
      state.measureToolTooltips = undefined
      state.measureToolGuides = undefined
      state.measureToolPopup = undefined
    }
  },
  setIsMeasureToolEnabled(state: BuilderState, action: PayloadAction<boolean>) {
    state.isMeasureToolEnabled = action.payload
  },
  setMeasureToolLayer(state: BuilderState, action: PayloadAction<FeatureCollection | undefined>) {
    state.measureToolLayer = action.payload
  },
  setMeasureToolTooltips(state: BuilderState, action: PayloadAction<MeasureTooltip[] | undefined>) {
    state.measureToolTooltips = action.payload
  },
  setMeasureToolGuides(state: BuilderState, action: PayloadAction<FeatureCollection | undefined>) {
    state.measureToolGuides = action.payload
  },
  setMeasureToolPopup(state: BuilderState, action: PayloadAction<MeasureToolPopup | undefined>) {
    state.measureToolPopup = action.payload
  },
  setAgentMarkerHover(
    state: BuilderState,
    action: PayloadAction<{ x: number; y: number; label: string; locationNumber: number } | undefined>
  ) {
    state.agentMarkerHover = action.payload
  },
  addTimeSeriesDataSourceAnimating(state: BuilderState, action: PayloadAction<string>) {
    state.timeSeriesDataSourceAnimating[action.payload] = true
  },
  removeTimeSeriesDataSourceAnimating(state: BuilderState, action: PayloadAction<string>) {
    delete state.timeSeriesDataSourceAnimating[action.payload]
  },
  setGeocoderSearchText(state: BuilderState, action: PayloadAction<string>) {
    state.geocoderSearchText = action.payload
  },
  setBasemapSettings(state: BuilderState, action: PayloadAction<BasemapSettings>) {
    state.basemapSettings = action.payload
  },
  setCustomBasemaps(state: BuilderState, action: PayloadAction<Basemap[]>) {
    state.customBasemaps = action.payload
  },
  setMapStyles(state: BuilderState, action: PayloadAction<BuilderBasemapStyle[]>) {
    state.mapStyles = action.payload
  },
  updateBasemapConfig(state: BuilderState, action: PayloadAction<Partial<BasemapConfig>>) {
    state.basemapConfig = { ...state.basemapConfig, ...action.payload }
    const selectedCustomBasemap = state.customBasemaps?.find(
      (b) => basemapIdToStyleId(b.id) === state.basemapConfig.styleId
    )
    state.customBaseMaps = {
      ...state.customBaseMaps,
      customStyle: selectedCustomBasemap ? basemapToMapStyle(selectedCustomBasemap) : undefined
    }
  },
  // TODO: This shouldn't be part of the builder state, but a local state of the React component responsible for displaying the LayerPanel (even a Context or just a drilled prop)
  // instead, we need this workaround because there's no mechanism for determining from the React component side, if the Saga has finished or not.
  setLayerPanelLoadingStatus(state: BuilderState, action: PayloadAction<RequestStatus>) {
    state.layerPanelLoadingStatus = action.payload
  },
  setMapStylesStatus(state: BuilderState, action: PayloadAction<RequestStatus>) {
    state.mapStylesStatus = action.payload
  },
  setTakeMapScreenshots: (state: BuilderState, action: PayloadAction<boolean>) => {
    const takeMapScreenshots = action.payload
    // Multiple process could require to take map screenshots at the same time and if the map is splitted we could have the
    // first screenshot ready and the second in process.
    if (takeMapScreenshots && !state.takeMapScreenshots) {
      state.mapScreenshots = []
    }
    state.takeMapScreenshots = takeMapScreenshots
  },
  setMapScreenshot: (
    state: BuilderState,
    action: PayloadAction<{ index: number; screenshot: string; isSplit: boolean }>
  ) => {
    state.mapScreenshots[action.payload.index] = action.payload.screenshot

    const stillNeedSnapshots = action.payload.isSplit
      ? !state.mapScreenshots[0] || !state.mapScreenshots[1]
      : !state.mapScreenshots[0]
    state.takeMapScreenshots = stillNeedSnapshots
  },
  updateAISettings: (state: BuilderState, action: PayloadAction<Partial<AISettings>>) => {
    state.aiSettings = {
      ...state.aiSettings,
      ...action.payload
    }
  },
  setAgentThread: (
    state: BuilderState,
    action: PayloadAction<{ thread?: BuilderAgentThread; conversation?: BuilderAgentConversation }>
  ) => {
    state.genAiThread = action.payload.thread
    state.genAiConversation = action.payload.conversation || []
  },
  updateAgentThread: (state: BuilderState, action: PayloadAction<Partial<BuilderAgentThread>>) => {
    if (state.genAiThread) {
      state.genAiThread = { ...state.genAiThread, ...action.payload }
    }
  },
  addAgentThreadDataset: (state: BuilderState, action: PayloadAction<BuilderAgentDataset>) => {
    if (state.genAiThread) {
      state.genAiThread.datasets.push(action.payload)
    }
  },
  addAgentThreadLayer: (state: BuilderState, action: PayloadAction<BuilderAgentLayer>) => {
    if (state.genAiThread) {
      state.genAiThread.layers.push(action.payload)
    }
  },
  removeAgentThreadDataset: (state: BuilderState, action: PayloadAction<{ datasetId: string }>) => {
    if (state.genAiThread) {
      state.genAiThread.datasets = state.genAiThread.datasets.filter((d) => d.datasetId !== action.payload.datasetId)
    }
  },
  removeAgentThreadLayer: (state: BuilderState, action: PayloadAction<{ layerId: string }>) => {
    if (state.genAiThread) {
      state.genAiThread.layers = state.genAiThread.layers.filter((d) => d.layerId !== action.payload.layerId)
    }
  },
  updateAgentThreadDataset: (
    state: BuilderState,
    action: PayloadAction<{ actionId: string; dataset: Partial<BuilderAgentDataset> }>
  ) => {
    // Update by actionId, not datasetId, in order to set error state if dataset creation fails.
    const idx = state.genAiThread?.datasets.findIndex((d) => d.actionId === action.payload.actionId) ?? -1
    if (state.genAiThread && idx >= 0) {
      state.genAiThread.datasets[idx] = {
        ...state.genAiThread.datasets[idx],
        ...action.payload.dataset
      } as BuilderAgentDataset
    }
  },
  updateAgentThreadLayer: (
    state: BuilderState,
    action: PayloadAction<{ actionId: string; layer: Partial<BuilderAgentLayer> }>
  ) => {
    // Update by actionId, not layerId, in order to set error state if layer creation fails.
    const idx = state.genAiThread?.layers.findIndex((d) => d.actionId === action.payload.actionId) ?? -1
    if (state.genAiThread && idx >= 0) {
      state.genAiThread.layers[idx] = { ...state.genAiThread.layers[idx], ...action.payload.layer } as BuilderAgentLayer
    }
  },
  addAgentMessage: (state: BuilderState, action: PayloadAction<BuilderAgentConversationItem>) => {
    const item = action.payload

    // For non-bot messages, simply add to conversation.
    if (item.author !== BuilderAgentAuthor.Bot) {
      state.genAiConversation.push(action.payload)
      return
    }

    // Bot messages are streamed; deltas are appended to previous deltas, and eventually
    // followed by a complete response that replaces a previous stack of deltas.
    const deltaIndex = getCurrentGenAIDeltaIndex(state.genAiConversation)
    const deltaMessage = state.genAiConversation[deltaIndex] as { type: 'delta'; message: string }

    if (deltaMessage && item.type === 'delta') {
      deltaMessage.message += item.message
    } else if (deltaMessage && item.type === 'response') {
      state.genAiConversation[deltaIndex] = item
    } else {
      state.genAiConversation.push(item)
    }
  },
  addLocationsToUserMessage: (
    state: BuilderState,
    action: PayloadAction<{
      date: string
      locations: Array<{ geometry: GeoJSON.Point; label: string; removed: boolean }>
    }>
  ) => {
    const userMessage = state.genAiConversation.find(
      (item) => item.author === BuilderAgentAuthor.User && item.date === action.payload.date
    )
    if (userMessage && userMessage.author === BuilderAgentAuthor.User) {
      userMessage.locations = [...(userMessage.locations || []), ...action.payload.locations]
    }
  },
  removeAllAgentConversationLocations: (state: BuilderState) => {
    state.genAiConversation.forEach((item) => {
      if (item.author === BuilderAgentAuthor.User && item.locations) {
        item.locations.forEach((location) => {
          location.removed = true
        })
      }
    })
  },
  nextAgentAction: (state: BuilderState) => {
    const conversation = state.genAiConversation
    const action = getFirstGenAIActionNotCompleted(conversation)
    if (action) {
      action.isCompleted = true
    }
  },
  setDynamicTilingMigrationBackup: (state: BuilderState, action: PayloadAction<DynamicTilingMigrationBackup>) => {
    state.dynamicTilingMigrationBackup = action.payload
  },
  setDragMapState: (state: BuilderState, action: PayloadAction<Partial<MapState> | undefined>) => {
    state.dragMapState = action.payload ? { ...state.dragMapState, ...action.payload } : undefined
  },
  setHighlighted: (
    state: BuilderState,
    action: PayloadAction<{ id: string | null; type: 'dataset' | 'layer' | null }>
  ) => {
    state.highlighted = action.payload
  },
  updateLayerState(state: BuilderState, action: PayloadAction<{ layerId: string; update: Partial<LayerState> }>) {
    const {
      layerId,
      update: { legendHexColorMaps, ...update }
    } = action.payload
    state.layerState[layerId] = {
      ...state.layerState[layerId],
      ...update,
      ...(legendHexColorMaps && {
        legendHexColorMaps: { ...state.layerState[layerId]?.legendHexColorMaps, ...legendHexColorMaps }
      })
    }
  },
  openAgentConversation: (state: BuilderState) => {
    state.shouldOpenAgentConversation = true
  },
  acknowledgeOpenAgentConversation: (state: BuilderState) => {
    state.shouldOpenAgentConversation = false
  }
}

function getFirstGenAIActionNotCompleted(conversation: BuilderAgentConversation) {
  for (const item of conversation) {
    const action =
      item.author === BuilderAgentAuthor.Bot &&
      item.type === 'action_frontend' &&
      item.actions.find((action) => !action.isCompleted)
    if (action) {
      return action
    }
  }
}

/**
 * Returns index of current 'delta' item onto which new deltas should be appended,
 * if any, otherwise returns -1. Deltas received after the last non-bot item, and
 * after the last bot 'response' item, are eligible.
 */
function getCurrentGenAIDeltaIndex(items: BuilderAgentConversation): number {
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i]
    if (item.author !== BuilderAgentAuthor.Bot) break
    if (item.type === 'response') break
    if (item.type !== 'delta') continue
    return i
  }
  return -1
}

export const builderAgentActionToExecute = createSelector(
  (state: RootState) => state.builder.genAiConversation,
  (conversation) => getFirstGenAIActionNotCompleted(conversation)
)

export const someTimeSeriesDataSourceAnimating = createSelector(
  (state: RootState) => state.builder.timeSeriesDataSourceAnimating,
  (timeSeriesDataSourceAnimating) => {
    for (const animating of Object.values(timeSeriesDataSourceAnimating)) {
      if (animating) return true
    }
    return false
  }
)

export const allDatasetsLoaded = createSelector(
  (state: RootState) => state.maps.currentMapDatasets,
  (datasets) => {
    return datasets.every((d) => d.status === RequestStatus.Fulfilled)
  }
)

export const isAutosavingEnabledSelector = createSelector(
  allDatasetsLoaded,
  someTimeSeriesDataSourceAnimating,
  blockAutoSaveDocMigrationTemporary,
  (state: RootState) => state.maps.currentMap,
  (state: RootState) => state.builder.isLoading,
  (state: RootState) => state.builder.builderMode,
  (state: RootState) => state.builder.previewMode,
  (
    allDatasetsLoadedAndHaveLayers,
    someTimeSeriesDataSourceAnimating,
    blockAutoSaveDocMigrationTemporary,
    currentMap,
    isLoading,
    builderMode,
    previewMode
  ) => {
    return Boolean(
      currentMap?.id &&
        (builderMode === BuilderMode.Editor || previewMode) &&
        !isLoading &&
        !blockAutoSaveDocMigrationTemporary &&
        allDatasetsLoadedAndHaveLayers &&
        !someTimeSeriesDataSourceAnimating
    )
  }
)

export const getLayerUpdateStatus = createSelector(
  (state: RootState) => state.builder.layersUpdateStatus,
  (_: unknown, layerId: string) => layerId,
  (layersUpdateStatus, layerId) => layersUpdateStatus[layerId] || RequestStatus.Idle
)

export const getDataSourceFilters = createSelector(
  (state: RootState) => state.carto.dataSources,
  (_: unknown, dataSourceId: string) => dataSourceId,
  (dataSources, dataSourceId) => {
    return dataSources?.[dataSourceId]?.filters
  }
)

export const widgetsMapConfigSelector = createSelector(
  (state: RootState) => state.builder.widgets,
  (state: RootState) => state.carto.dataSources,
  selectValidSpatialFilter,
  (allWidgets, cartoDataSources, spatialFilter) => {
    // See: https://github.com/CartoDB/cloud-native/pull/17086
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const widgets = allWidgets.filter((w) => w.isValid).map(({ state, ...w }) => w)
    const filters = Object.keys(cartoDataSources).reduce((filters, dataSource) => {
      const _filters = cartoDataSources[dataSource].filters || {}
      const filtersWithoutTimeType = getFiltersWithoutTimeType(_filters)

      if (Object.keys(filtersWithoutTimeType).length >= 0) {
        filters[dataSource] = filtersWithoutTimeType
      }
      return filters
    }, {})

    return { widgets, filters, spatialFilter } as WidgetsMapConfig
  }
)

export const builderPersistedStateSelector = createSelector(
  (state: RootState) => state.builder,
  (state: RootState) => state.mapComments.commentsVisible,
  widgetsMapConfigSelector,
  basemapConfigSelector,
  (builderState, commentsVisible, { widgets, filters, spatialFilter }, basemapConfig) => {
    const {
      customBaseMaps,
      legendSettings,
      popupSettings,
      sqlParameters,
      descriptionOpen,
      descriptionPreview,
      controlsPaneOpen,
      controlsPaneTab,
      aiSettings,
      dynamicTilingMigrationBackup
    } = builderState

    return {
      basemapConfig,
      widgets,
      filters,
      spatialFilter,
      ...(customBaseMaps !== undefined && { customBaseMaps }),
      ...(legendSettings !== undefined && { legendSettings }),
      popupSettings,
      sqlParameters,
      aiSettings,
      uiState: {
        descriptionOpen,
        descriptionPreview,
        controlsPaneOpen,
        ...(controlsPaneTab !== undefined && { controlsPaneTab }),
        commentsVisible
      },
      dynamicTilingMigrationBackup
    }
  }
)

function applyBuilderStateToKeplerConfig(keplerConfig: SavedConfigV1, layerState: Record<string, LayerState>) {
  return {
    ...keplerConfig,
    config: {
      ...keplerConfig.config,
      visState: {
        ...keplerConfig.config.visState,
        layers: keplerConfig.config.visState.layers.map((layer) => ({
          ...layer,
          ...(layerState[layer.id]?.legendHexColorMaps && {
            legendHexColorMaps: layerState[layer.id].legendHexColorMaps
          })
        }))
      }
    }
  }
}

export const mapConfigSelector = createSelector(
  (state: RootState) => state.keplerGl.map,
  builderPersistedStateSelector,
  (state: RootState) => state.maps.mapSettings,
  (state: RootState) => state.builder.layerState,
  (state: RootState) => state.builder.genAiThread?.layers,
  (keplerGlState, persistedBuilderState, mapSettings, layerState, agentLayers) => {
    const agentLayerIds = agentLayers ? (agentLayers.map((d) => d.layerId).filter(Boolean) as string[]) : []
    const keplerConfig = applyBuilderStateToKeplerConfig(KeplerGlSchema.getConfigToSave(keplerGlState), layerState)

    return removeAgentLayersFromMapConfig(
      {
        ...keplerConfig,
        config: {
          ...keplerConfig.config,
          // This is only for cross-compatibilitty with old Builder versions that may
          // try to load this map, can be removed after PR with this change is considered stable.
          mapStyle: {
            visibleLayerGroups: persistedBuilderState.basemapConfig.visibleLayerGroups,
            styleType: persistedBuilderState.basemapConfig.styleId
          },
          mapSettings,
          ...persistedBuilderState
        }
      },
      agentLayerIds
    )
  }
)

export const mapConfigHashSelector = createSelector(mapConfigSelector, getValueHash)

export function filterRightPaneWidgets(widgets: Widget[]): Widget[] {
  return widgets.filter((w) => {
    if (w.type === WidgetType.TIMESERIES) {
      return widgets.some((otherWidget) => otherWidget.type === WidgetType.TABLE)
    }
    return w.type !== WidgetType.TABLE
  })
}

export function filterBottomPaneWidgets(widgets: Widget[]): Widget[] {
  return widgets.filter((w) => {
    if (w.type === WidgetType.TIMESERIES) {
      return widgets.some((otherWidget) => otherWidget.type !== WidgetType.TABLE)
    }
    return w.type === WidgetType.TABLE
  })
}

export const builderSlice = createSlice({
  name,
  initialState,
  reducers
})

export const {
  setMapPasswordRequired,
  incMapPasswordEnterAttempt,
  setPublicMapAuthorizationHeader,
  showSQLPanel,
  setSQLPanelMinimized,
  hideSQLPanel,
  setDraftQuery,
  resetBuilderState,
  setIsLoading,
  setSaveNeeded,
  resetSaveNeeded,
  setSaveMapError,
  setBuilderMode,
  setPreviewMode,
  setLastThumbnailMapConfigHash,
  setWidgets,
  removeWidget,
  updateWidgetProps,
  updateWidgetWrapperProps,
  replaceWidget,
  removeWidgetsByDataSource,
  setSelectedWidgetId,
  setTilesetWidgetSourceReady,
  setControlsPaneOpen,
  setControlsPaneState,
  setRightPaneTab,
  setCustomBaseMapSettings,
  setLegendSettings,
  setLayerLegendSettings,
  addDatasetAttributeStatsOnRequest,
  removeDatasetAttributeStatsOnRequest,
  setPopupSettings,
  setLayerPopupSettings,
  moveLayerPopupSettings,
  setFeatureDetailsPanel,
  closeFeatureDetailsPanel,
  loadSqlParameters,
  addSqlParameter,
  updateSqlParameter,
  removeSqlParameter,
  setCurrentMapLockStatus,
  updateCurrentMapLockStatus,
  setTakeOverResponseState,
  setLayerUpdateStatus,
  setViewerCredentialsRequired,
  setUserLocation,
  setDescriptionOpen,
  setDescriptionPreview,
  setIsMeasureToolEnabled,
  setMeasureToolLayer,
  setMeasureToolTooltips,
  setMeasureToolGuides,
  setMeasureToolPopup,
  setAgentMarkerHover,
  disableMeasureTool,
  addTimeSeriesDataSourceAnimating,
  removeTimeSeriesDataSourceAnimating,
  setGeocoderSearchText,
  setLayerPanelLoadingStatus,
  setBasemapSettings,
  setCustomBasemaps,
  setMapStyles,
  setMapStylesStatus,
  updateBasemapConfig,
  setTakeMapScreenshots,
  setMapScreenshot,
  updateAISettings,
  setAgentThread,
  updateAgentThread,
  addAgentThreadDataset,
  addAgentThreadLayer,
  removeAgentThreadDataset,
  removeAgentThreadLayer,
  updateAgentThreadDataset,
  updateAgentThreadLayer,
  addAgentMessage,
  addLocationsToUserMessage,
  removeAllAgentConversationLocations,
  nextAgentAction,
  setDynamicTilingMigrationBackup,
  setDragMapState,
  setHighlighted,
  updateLayerState,
  openAgentConversation,
  acknowledgeOpenAgentConversation
} = builderSlice.actions

export const builderSliceActions = builderSlice.actions

export const {
  initBuilderMap,
  addLayerRequest,
  addLayerToExistingDatasetRequest,
  addQueryRequest,
  updateDatasetsWithSqlParametersRequest,
  updateQueryRequest,
  cancelQueryRequest,
  refreshDatasetRequest,
  refreshDatasetsByConnections,
  attachViewerCredentials,
  addLayerFromImportedJobRequest,
  newWidget,
  removeFilterByWidgetRequest,
  loadAttributeStatsRequest,
  loadHexColumnStatsRequest,
  updateSpatialIndexAggregationRequest,
  unlockMapRequest,
  updateSpatialIndexAggregationResLevelRequest,
  queryThisTableRequest,
  convertTableToQueryDatasetRequest,
  aggregateDatasetByGeometry,
  updateLayerFiltersForTimeSeriesAnimationRequest,
  mapPasswordUpdated,
  legendVisibilityChange,
  duplicateDatasetRequest
} = asyncActions

export const getWidgetsByDataset = createSelector(
  (state: RootState) => state.builder.widgets,
  (_: unknown, datasetId: string) => datasetId,
  (widgets, datasetId) => widgets.filter((w) => w.dataSource === datasetId)
)

export const getDataSourcesIncompatibleWithWidgets = createSelector(
  (state: RootState) => state.maps.currentMapDatasets,
  (state: RootState) => state.keplerGl.map.visState.layers,
  (dataSources, layers) => {
    return dataSources.filter((dataSource) => {
      const { compatible } = checkDataSourceWidgetCompatibility({
        layers,
        source: dataSource
      })

      return !compatible
    })
  }
)

export const getWidgetById = createSelector(
  (state: RootState) => state.builder.widgets,
  (_: unknown, widgetId: string) => widgetId,
  (widgets, widgetId: string): Widget | undefined => widgets.find((widget) => widget.id === widgetId)
)

export const getWidgetStateById = createSelector(
  getWidgetById,
  (widget) => widget?.state || { state: WidgetStateType.Loading }
)
// TODO: Find a proper place for this
const keplerDatasets = (state: RootState) => state.keplerGl.map?.visState?.datasets
const datasets = (state: RootState) => state.maps.currentMapDatasets

export const builderDatasetsSelector = createSelector(
  datasets,
  keplerDatasets,
  (currentMapDatasets, keplerDatasets): BuilderDataset[] => {
    return currentMapDatasets.map((mapDataset, index) => {
      const keplerDataset = keplerDatasets[mapDataset.id] as KeplerTable | undefined
      const tilejson = keplerDataset?.metadata?.tilejson
      const tilestats = keplerDataset?.metadata?.tilestats

      // seems like we keep copy of tilestats with _attributes_ updated as we load
      // stats, use those instead of those originally loaded from tilejson
      // (or invent better mechanism)
      const tilejsonWithTilestats = {
        ...tilejson,
        ...(tilestats &&
          !tilejson?.raster_metadata && {
            tilestats: {
              ...tilejson?.tilestats,
              layers: [tilestats]
            }
          })
      }
      const schema = keplerDataset?.metadata.schema
      return {
        ...mapDataset,
        letter: String.fromCharCode(65 + index),
        color: mapDataset.color || getNewDatasetColor(currentMapDatasets),
        tilejson: tilejsonWithTilestats,
        schema,
        metadata: keplerDataset?.metadata
      } as BuilderDataset
    })
  }
)

export const builderNonAgentDatasetsSelector = createSelector(
  builderDatasetsSelector,
  (state: RootState) => state.builder.genAiThread?.datasets,
  (datasets, agentDatasets) => {
    if (!agentDatasets?.length) return datasets
    const agentDatasetIds = new Set(agentDatasets.map((d) => d.datasetId))
    return datasets.filter((d) => !agentDatasetIds.has(d.id))
  }
)

export const builderDatasetSelector = createEntitySelector(
  builderDatasetsSelector,
  (_: unknown, datasetId: string) => datasetId,
  (datasets, datasetId): BuilderDataset | undefined => {
    return datasets.find((d) => d.id === datasetId)
  }
)

export const getBuilderDatasetForLayer = createSelector(
  builderDatasetsSelector,
  (_: unknown, layer: Layer) => layer,
  (datasets, layer): BuilderDataset | undefined => {
    return datasets.find((dataset) => dataset.id === layer.config.dataId)
  }
)

export const getBuilderDatasetById = createSelector(
  [builderDatasetsSelector, (_: unknown, datasetId: string | undefined | null) => datasetId],
  (datasets, datasetId): BuilderDataset | undefined => {
    return datasetId ? datasets.find((d) => d.id === datasetId) : undefined
  }
)

export const getEnrichedDatasetById = createSelector(
  [keplerDatasets, datasets, (_: unknown, datasetId: string) => datasetId],
  (keplerDatasets, datasets, datasetId) => {
    const dataset = datasets.find((d) => d.id === datasetId)
    return datasetId in keplerDatasets ? { ...dataset, ...keplerDatasets[datasetId] } : null
  }
)

export const isMapToolEnabledSelector = createSelector(
  (state: RootState) => state.carto.featureSelectionEnabled,
  (state: RootState) => state.builder.isMeasureToolEnabled,
  (state: RootState) => state.mapComments.commentsOpen,
  (featureSelectionEnabled, isMeasureToolEnabled, commentsOpen) =>
    featureSelectionEnabled || isMeasureToolEnabled || commentsOpen
)

export const builderLayerStateSelector = createSelector(
  (state: RootState) => state.builder.layerState,
  (_: unknown, layerId: string) => layerId,
  (layerState, layerId): LayerState | undefined => layerState[layerId]
)

const getMemoizedBuilderLayer = createMemoizeById<BuilderLayerWithData>()
export const builderLayersSelector = createSelector(
  [
    (state: RootState) => state.keplerGl.map.visState.layers,
    (state: RootState) => state.builder.layerState,
    builderDatasetsSelector
  ],
  (layers: KeplerLayer[], layerStates, datasets: BuilderDataset[]): BuilderLayerWithData[] =>
    (layers || []).map((layer) => {
      // we really don't support layers without datasets, but we need to type it as MapDataset
      // to avoid type errors
      const layerState = layerStates?.[layer.id]
      const dataset = datasets.find((dataset) => dataset.id === layer.config.dataId) as BuilderDataset
      const serializedLayer = serializedKeplerLayerMemoized(layer)
      return getMemoizedBuilderLayer({
        ...serializedLayer,
        mapDataset: dataset,
        data: dataset?.tilejson || undefined,
        legendHexColorMaps: layerState?.legendHexColorMaps
      })
    })
)

export const builderOrderedLayersSelector = createSelector(
  [(state: RootState) => state.keplerGl?.map?.visState?.layerOrder, builderLayersSelector],
  (layerOrder, layers): BuilderLayerWithData[] => {
    return layerOrder.map((layerIndex) => layers[layerIndex])
  }
)

export const builderLayerSelector = createEntitySelector(
  [builderLayersSelector, (_: unknown, layerId: string) => layerId],
  (layers, layerId) => layers.find((layer) => layer.id === layerId),
  {
    memoizeOptions: {
      equalityCheck: shallowEqual
    }
  }
)

export const builderLayerIdsSelector = createSelector(
  (state: RootState) => state.keplerGl.map?.visState?.layers,
  (layers) => (layers ? layers.map((layer) => layer.id) : []),
  {
    memoizeOptions: {
      equalityCheck: isEqual
    }
  }
)

export default builderSlice.reducer
